import { getAuth } from "firebase/auth"
import pako from "pako"
import { defineStore } from "pinia"
import { FirebaseTokenLoader, Job, JobsService, JobStatus, SDKObject } from "@telarya/telarya-sdk"
import { toRaw } from "vue"

// eslint-disable-next-line camelcase
import { HACK_JobAddBuySellOrders, HACK_JobArrayAddBuySellOrders } from "@/components/Jobs/HACK_JobAddBuySellOrders"
import generateEndpoint from "@/endpoint"
import { createAuthenticatedHttpClient } from "@/services/httpClient"
import { reportError } from "@/utils/reportError"

const storeObject = "Jobs"
const storeName = storeObject + "Store"

export const useTradeJobsStore = defineStore("tradeJobs", {
  state: () => ({
    jobsByTenant: {}, // Structure: { [tenantId]: { [rangeKey]: jobs } }
    isFetching: {}, // Structure: { [tenantId]: { [rangeKey]: true/false } }
    rangeLastFetched: {}, // Structure: { [tenantId]: { [rangeKey]: timestamp } }
    sdkService: null,
    isInitialized: false,
    tenantId: null,
    pusher: null,
    pusherChannelName: null,
    pusherChannel: null,
    channelHandlers: null,
    connectionHandlers: null,
    overdueJobsCount: null, // Holds overdue jobs count
    onHoldJobsCount: null, // Holds on-hold jobs count
    overdueJobsLastFetched: null, // Timestamp of the last overdue jobs fetch
    onHoldJobsLastFetched: null, // Timestamp of the last on-hold jobs fetch
    lastFetchedRanges: {}, // Tracks the last fetched range for each tenant
    lastConnectionTimestamp: null, // Timestamp of last known good connection
    isOnline: true, // Current online status
    potentialDataLoss: false // Flag to indicate potential data loss during disconnection
  }),

  actions: {
    initializeSdkService() {
      if (this.sdkService) {
        return
      }
      this.sdkService = new JobsService(
        generateEndpoint("jobs"),
        createAuthenticatedHttpClient(new FirebaseTokenLoader(getAuth()))
      )
      this.subscribeToPusher()
      this.setupConnectionListeners()
      this.isInitialized = true
      this.lastConnectionTimestamp = Date.now()
    },
    teardownPusher() {
      if (this.pusherChannel) {
        if (this.channelHandlers) {
          Object.entries(this.channelHandlers).forEach(([event, handler]) => {
            this.pusherChannel.unbind(event, handler)
          })
        }
        this.pusherChannel.unbind_all()
      }
      if (this.pusher && this.pusherChannelName) {
        this.pusher.unsubscribe(this.pusherChannelName)
      }
      if (this.pusher && this.connectionHandlers) {
        Object.entries(this.connectionHandlers).forEach(([event, handler]) => {
          this.pusher.connection.unbind(event, handler)
        })
      }
      this.pusherChannelName = null
      this.pusherChannel = null
      this.channelHandlers = null
      this.connectionHandlers = null
    },

    resetState() {
      this.teardownPusher()
      this.cleanup()
      this.jobsByTenant = {}
      this.isFetching = {}
      this.rangeLastFetched = {}
      this.sdkService = null
      this.isInitialized = false
      this.tenantId = null
      this.pusher = null
      this.overdueJobsCount = null
      this.onHoldJobsCount = null
      this.overdueJobsLastFetched = null
      this.onHoldJobsLastFetched = null
      this.lastFetchedRanges = {}
      this.lastConnectionTimestamp = null
      this.isOnline = true
      this.potentialDataLoss = false
    },

    initialize(tenant, webSocket) {
      if (!tenant || !tenant.id) {
        throw new Error(`${storeName}: Invalid tenant provided.`)
      }
      if (!tenant || !tenant.id) {
        throw new Error(`${storeName}: Invalid tenant provided during initialization.`)
      }
      if (!webSocket) {
        throw new Error(`${storeName}: Invalid webSocket service provided during initialization.`)
      }
      const tenantChanged = this.tenantId && this.tenantId !== tenant.id
      if (tenantChanged) {
        this.resetState()
      } else if (this.tenantId === tenant.id && this.pusher) {
        return
      }
      this.tenantId = tenant.id
      this.pusher = toRaw(webSocket)
    },

    setupConnectionListeners() {
      // Listen for online/offline events
      window.addEventListener("online", this.handleConnectionChange)
      window.addEventListener("offline", this.handleConnectionChange)

      // Listen for visibility change (detect wake from sleep)
      document.addEventListener("visibilitychange", this.handleVisibilityChange)
    },

    handleConnectionChange() {
      const isCurrentlyOnline = navigator.onLine
      this.logToConsole(`Connection status changed: ${isCurrentlyOnline ? "online" : "offline"}`, "info")

      // If going offline, mark potential data loss
      if (!isCurrentlyOnline && this.isOnline) {
        this.potentialDataLoss = true
      }

      // Update online status
      this.isOnline = isCurrentlyOnline
      if (isCurrentlyOnline) {
        this.lastConnectionTimestamp = Date.now()
      }
    },

    handleVisibilityChange() {
      // If document becomes visible again (potential wake from sleep)
      if (document.visibilityState === "visible") {
        const lastActive = this.lastConnectionTimestamp || 0
        const timeSinceActive = Date.now() - lastActive

        // If it's been more than 1 minute since we were last active, consider it a wake from sleep
        if (timeSinceActive > 60000) {
          this.logToConsole(
            `Device potentially woke from sleep after ${(timeSinceActive / 1000).toFixed(1)} seconds inactive`,
            "info"
          )
          this.potentialDataLoss = true
        }

        this.lastConnectionTimestamp = Date.now()
      }
    },

    async refetchRange(tenantId, startDate, endDate) {
      const rangeKey = `${startDate.toISOString()}_${endDate.toISOString()}`
      this.logToConsole(`Refetching potentially stale range: ${rangeKey} for tenant ${tenantId}`, "info")

      if (this.isFetching[tenantId]?.[rangeKey]) {
        this.logToConsole(`Range ${rangeKey} is already being fetched, skipping`, "info")
        return
      }

      try {
        // Set fetching flag
        if (!this.isFetching[tenantId]) {
          this.isFetching[tenantId] = {}
        }
        this.isFetching[tenantId][rangeKey] = true

        // Fetch fresh data
        const jobs = await this.sdkService.getTradeJobs(undefined, startDate, endDate)
        HACK_JobArrayAddBuySellOrders(jobs, { id: tenantId })

        // Update the cache
        if (!this.jobsByTenant[tenantId]) {
          this.jobsByTenant[tenantId] = {}
        }
        this.jobsByTenant[tenantId][rangeKey] = jobs

        // Update the last fetched timestamp
        if (!this.rangeLastFetched[tenantId]) {
          this.rangeLastFetched[tenantId] = {}
        }
        this.rangeLastFetched[tenantId][rangeKey] = Date.now()

        this.logToConsole(`Successfully refetched range ${rangeKey} for tenant ${tenantId}`, "info")
      } catch (error) {
        reportError(error, {
          message: "Failed to refetch jobs range",
          source: "TradeJobsStore.refetchRange",
          tenantId,
          rangeKey
        })
      } finally {
        if (this.isFetching[tenantId]) {
          this.isFetching[tenantId][rangeKey] = false
        }
      }
    },

    isRangePotentiallyStale(tenantId, rangeKey) {
      // No timestamp for this range yet (should not happen, but being cautious)
      if (!this.rangeLastFetched[tenantId]?.[rangeKey]) {
        return true
      }

      // Range was fetched before the potential data loss occurred
      return this.potentialDataLoss && this.rangeLastFetched[tenantId][rangeKey] < this.lastConnectionTimestamp
    },

    async getJobs(tenantId, startDate, endDate) {
      this.initializeSdkService()

      if (!this.jobsByTenant[tenantId]) {
        this.jobsByTenant[tenantId] = {}
        this.isFetching[tenantId] = {}
        this.rangeLastFetched[tenantId] = {}
      }

      const rangeKey = `${startDate.toISOString()}_${endDate.toISOString()}`

      // Store the last fetched range for the tenant
      this.lastFetchedRanges[tenantId] = { startDate, endDate }

      // Check if we have cached data
      if (this.jobsByTenant[tenantId][rangeKey]) {
        // Check if this range is potentially stale due to sleep/disconnection
        if (this.isRangePotentiallyStale(tenantId, rangeKey)) {
          this.logToConsole(
            `Range ${rangeKey} for tenant ${tenantId} may be stale. Serving cached data and refreshing in background.`,
            "info"
          )

          // Return cached data immediately but schedule a background refresh
          setTimeout(() => {
            this.refetchRange(tenantId, startDate, endDate)
          }, 0)
        }

        return this.jobsByTenant[tenantId][rangeKey]
      }

      // If we're already fetching this range, return empty array
      if (this.isFetching[tenantId][rangeKey]) {
        return []
      }

      this.isFetching[tenantId][rangeKey] = true

      try {
        const jobs = await this.sdkService.getTradeJobs(undefined, startDate, endDate)
        HACK_JobArrayAddBuySellOrders(jobs, { id: tenantId })

        this.jobsByTenant = {
          ...this.jobsByTenant,
          [tenantId]: {
            ...this.jobsByTenant[tenantId],
            [rangeKey]: jobs
          }
        }

        // Store the fetch timestamp
        if (!this.rangeLastFetched[tenantId]) {
          this.rangeLastFetched[tenantId] = {}
        }
        this.rangeLastFetched[tenantId][rangeKey] = Date.now()

        return jobs
      } catch (error) {
        reportError(error, {
          message: "Failed to fetch jobs for tenant range",
          source: "TradeJobsStore.getJobs",
          tenantId,
          rangeKey
        })
        return []
      } finally {
        this.isFetching[tenantId][rangeKey] = false
      }
    },

    async getJobById(tenantId, jobId) {
      this.initializeSdkService()

      // 1) Try to find in cache
      if (this.jobsByTenant[tenantId]) {
        for (const jobs of Object.values(this.jobsByTenant[tenantId])) {
          const found = jobs.find((j) => j.id === jobId)
          if (found) {
            return found
          }
        }
      }

      // 2) Not in cache → fetch from backend
      const raw = await this.sdkService.getJob(jobId)
      HACK_JobAddBuySellOrders(raw, { id: tenantId })

      // 3) (optional) insert into your "last fetched range" cache if you want:
      const { startDate, endDate } = this.lastFetchedRanges[tenantId] || {}
      if (startDate && endDate) {
        const key = `${startDate.toISOString()}_${endDate.toISOString()}`
        this.jobsByTenant[tenantId] = {
          ...this.jobsByTenant[tenantId],
          [key]: [...(this.jobsByTenant[tenantId][key] || []), raw]
        }
      }

      return raw
    },

    subscribeToPusher() {
      if (!this.tenantId) {
        throw new Error(`${storeName}: Tenant ID is required for Pusher subscription.`)
      }

      if (!this.pusher) {
        throw new Error(`${storeName}: No webSocket service.`)
      }

      this.teardownPusher()

      const channelName = `${this.tenantId}_${storeObject}`
      const channel = toRaw(this.pusher).subscribe(channelName)
      this.pusherChannelName = channelName
      this.pusherChannel = channel

      this.channelHandlers = {
        Create: async (data) => {
          this.logToConsole(`Pusher message received on channel ${channelName} (Create):`, "info")

          let instance

          if (data.compressedData) {
            try {
              const compressedBytes = Uint8Array.from(atob(data.compressedData), (c) => c.charCodeAt(0))
              const decompressedBytes = pako.inflate(compressedBytes)
              const decompressedJson = new TextDecoder("utf-8").decode(decompressedBytes)
              const parsedJson = lowercaseKeys(JSON.parse(decompressedJson))
              instance = new Job()
              instance.init(parsedJson)
              instance = SDKObject.ProxyCreator(instance)
              console.log("Decompressed job instance:", instance)
            } catch (error) {
              reportError(error, {
                message: "Failed to decompress job data",
                source: "TradeJobsStore.subscribeToPusher.Create",
                tenantId: this.tenantId
              })
              this.logToConsole(`Failed to decompress data: ${error.message}`, "error")
              return
            }
          } else {
            instance = await this.sdkService.getJob(data.id)
          }

          HACK_JobAddBuySellOrders(instance, { id: this.tenantId })
          if (Array.isArray(instance.datesAll) && instance.datesAll.length > 0) {
            const jobDates = instance.datesAll.map((date) => new Date(date))

            for (const [tenantId, ranges] of Object.entries(this.jobsByTenant)) {
              for (const [rangeKey, jobs] of Object.entries(ranges)) {
                const [start, end] = rangeKey.split("_").map((date) => new Date(date))
                const isDateInRange = jobDates.some((jobDate) => jobDate >= start && jobDate <= end)

                if (isDateInRange) {
                  if (!this.jobsByTenant[tenantId][rangeKey].some((job) => job.id === instance.id)) {
                    this.jobsByTenant[tenantId][rangeKey] = [...this.jobsByTenant[tenantId][rangeKey], instance]
                    this.logToConsole(`Added new job to tenant ${tenantId}, range ${rangeKey}.`, "info")
                  }
                }
              }
            }
          }

          this.lastConnectionTimestamp = Date.now()
          this.potentialDataLoss = false
        },
        Delete: async (data) => {
          this.logToConsole(`Pusher message received on channel ${channelName} (Delete):`, "info")

          if (Array.isArray(data.datesAll) && data.datesAll.length > 0) {
            const jobDates = data.datesAll.map((date) => new Date(date))

            for (const [tenantId, ranges] of Object.entries(this.jobsByTenant)) {
              for (const [rangeKey, jobs] of Object.entries(ranges)) {
                const [start, end] = rangeKey.split("_").map((date) => new Date(date))
                const isDateInRange = jobDates.some((jobDate) => jobDate >= start && jobDate <= end)

                if (isDateInRange) {
                  const jobIndex = this.jobsByTenant[tenantId][rangeKey].findIndex((job) => job.id === data.id)

                  if (jobIndex !== -1) {
                    this.jobsByTenant[tenantId][rangeKey].splice(jobIndex, 1)
                    this.logToConsole(`Deleted job from tenant ${tenantId}, range ${rangeKey}.`, "info")
                  }
                }
              }
            }
          }

          this.lastConnectionTimestamp = Date.now()
          this.potentialDataLoss = false
        },
        Update: async (data) => {
          this.logToConsole(`Pusher message received on channel ${channelName} (Update):`, "info")

          let updatedJob

          if (data.compressedData) {
            try {
              const compressedBytes = Uint8Array.from(atob(data.compressedData), (c) => c.charCodeAt(0))
              const decompressedBytes = pako.inflate(compressedBytes)
              const decompressedJson = new TextDecoder("utf-8").decode(decompressedBytes)
              const parsedJson = lowercaseKeys(JSON.parse(decompressedJson))
              updatedJob = new Job()
              updatedJob.init(parsedJson)
              updatedJob = SDKObject.ProxyCreator(updatedJob)
              console.log("Decompressed updated job instance:", updatedJob)
            } catch (error) {
              reportError(error, {
                message: "Failed to decompress updated job data",
                source: "TradeJobsStore.subscribeToPusher.Update",
                tenantId: this.tenantId
              })
              this.logToConsole(`Failed to decompress update data: ${error.message}`, "error")
              return
            }
          } else {
            updatedJob = await this.sdkService.getJob(data.id)
          }

          HACK_JobAddBuySellOrders(updatedJob, { id: this.tenantId })

          for (const [tenantId, ranges] of Object.entries(this.jobsByTenant)) {
            for (const [rangeKey, jobs] of Object.entries(ranges)) {
              const [startStr, endStr] = rangeKey.split("_")
              const start = new Date(startStr)
              const end = new Date(endStr)
              const jobDates = Array.isArray(updatedJob.datesAll) ? updatedJob.datesAll.map((date) => new Date(date)) : []
              const isInRange = jobDates.some((jobDate) => jobDate >= start && jobDate <= end)

              const existingIndex = jobs.findIndex((job) => job.id === updatedJob.id)
              if (existingIndex !== -1 && !isInRange) {
                this.jobsByTenant[tenantId][rangeKey].splice(existingIndex, 1)
                this.logToConsole(`Removed job ${updatedJob.id} from tenant ${tenantId}, range ${rangeKey}.`, "info")
              }
              if (isInRange && existingIndex === -1) {
                this.jobsByTenant[tenantId][rangeKey] = [...this.jobsByTenant[tenantId][rangeKey], updatedJob]
                this.logToConsole(`Added updated job ${updatedJob.id} to tenant ${tenantId}, range ${rangeKey}.`, "info")
              }
            }
          }

          this.lastConnectionTimestamp = Date.now()
          this.potentialDataLoss = false
        },
        subscriptionSucceeded: () => {
          this.logToConsole(`Successfully subscribed to Pusher channel: ${channelName}`, "info")
          this.lastConnectionTimestamp = Date.now()
          this.potentialDataLoss = false
        },
        subscriptionError: (error) => {
          this.logToConsole(`Error subscribing to Pusher channel ${channelName}: ${error}`, "error")
          this.potentialDataLoss = true
        }
      }

      channel.bind("Create", this.channelHandlers.Create)
      channel.bind("Delete", this.channelHandlers.Delete)
      channel.bind("Update", this.channelHandlers.Update)
      channel.bind("pusher:subscription_succeeded", this.channelHandlers.subscriptionSucceeded)
      channel.bind("pusher:subscription_error", this.channelHandlers.subscriptionError)

      this.connectionHandlers = {
        disconnected: () => {
          this.logToConsole("Pusher disconnected", "info")
          this.potentialDataLoss = true
        },
        connected: () => {
          this.logToConsole("Pusher connected", "info")
          this.lastConnectionTimestamp = Date.now()
        }
      }

      this.pusher.connection.bind("disconnected", this.connectionHandlers.disconnected)
      this.pusher.connection.bind("connected", this.connectionHandlers.connected)

      this.logToConsole(`Subscribed to Pusher channel: ${channelName}`, "info")
    },

    async refreshLastFetchedRange() {
      if (!this.tenantId || !this.lastFetchedRanges[this.tenantId]) {
        console.warn("No last fetched range available to refresh.")
        return
      }

      const { startDate, endDate } = this.lastFetchedRanges[this.tenantId]
      this.logToConsole(
        `Refreshing last fetched range for tenant ${
          this.tenantId
        }: ${startDate.toISOString()} - ${endDate.toISOString()}`
      )

      try {
        const refreshedJobs = await this.getJobs(this.tenantId, startDate, endDate)
        const rangeKey = `${startDate.toISOString()}_${endDate.toISOString()}`
        this.jobsByTenant[this.tenantId][rangeKey] = refreshedJobs
        this.logToConsole(`Successfully refreshed jobs for tenant ${this.tenantId}.`, "info")
      } catch (error) {
        reportError(error, {
          message: "Failed to refresh last fetched range",
          source: "TradeJobsStore.refreshLastFetchedRange",
          tenantId: this.tenantId
        })
      }
    },

    logToConsole(m, type = "info") {
      console.log(storeName + " " + `[${type.toUpperCase()}] ${m}`)
    },

    async fetchOverdueJobsCount() {
      const now = new Date()
      if (
        this.overdueJobsLastFetched &&
        now - this.overdueJobsLastFetched < 12 * 60 * 60 * 1000 // 12 hours
      ) {
        return this.overdueJobsCount
      }

      try {
        this.overdueJobsCount = await this.sdkService.getOverdueJobsCount()
        this.overdueJobsLastFetched = now
      } catch (error) {
        reportError(error, {
          message: "Failed to fetch overdue jobs count",
          source: "TradeJobsStore.fetchOverdueJobsCount",
          tenantId: this.tenantId
        })
      }
      return this.overdueJobsCount
    },

    async fetchOnHoldJobsCount() {
      const now = new Date()
      if (
        this.onHoldJobsLastFetched &&
        now - this.onHoldJobsLastFetched < 3 * 60 * 60 * 1000 // 3 hours
      ) {
        return this.onHoldJobsCount
      }

      try {
        this.onHoldJobsCount = await this.sdkService.getJobsCount([JobStatus.OnHold])
        this.onHoldJobsLastFetched = now
      } catch (error) {
        reportError(error, {
          message: "Failed to fetch on-hold jobs count",
          source: "TradeJobsStore.fetchOnHoldJobsCount",
          tenantId: this.tenantId
        })
      }
      return this.onHoldJobsCount
    },

    // Clean up event listeners when store is no longer needed
    cleanup() {
      window.removeEventListener("online", this.handleConnectionChange)
      window.removeEventListener("offline", this.handleConnectionChange)
      document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    }
  }
})

function lowercaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map(lowercaseKeys)
  } else if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), lowercaseKeys(value)])
    )
  } else {
    return obj
  }
}
